import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Settings, 
  Bell, 
  HelpCircle, 
  LogOut, 
  ChevronRight,
  Heart,
  MapPin,
  Star,
  Shield,
  Moon,
  Menu,
  ChevronLeft,
  X,
  Check,
  Mail,
  Phone,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  Download,
  MessageCircle,
  FileText,
  ExternalLink,
  Sun,
  Camera,
  Edit3,
  Grid3X3,
  Bookmark,
  Play,
  Image,
  MessageSquare,
  Users,
  LayoutGrid,
  CheckCircle2,
  Route,
  MoreHorizontal,
  List,
  Send,
  Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { userApi, favoritesApi, tripsApi, communityApi, messagesApi, notificationsApi } from '../services/api';

const SubPageHeader = ({ title, onBack, rightAction, bgCard, borderColor, darkMode, textPrimary }) => (
  <div className={`${bgCard} border-b ${borderColor} sticky top-0 z-10`}>
    <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
      <button onClick={onBack} className={`p-2 -ml-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full`}>
        <ChevronLeft className={`w-6 h-6 ${textPrimary}`} />
      </button>
      <h1 className={`text-lg font-semibold ${textPrimary}`}>{title}</h1>
      <div className="w-10">{rightAction}</div>
    </div>
  </div>
);

const ProfilePage = ({ onTabChange }) => {
  const navigate = useNavigate();
  const { user, setUser, isAuthenticated, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  
  // States
  const [stats, setStats] = useState({
    posts: 0,
    followers: 0,
    following: 0
  });
  const [activityStats, setActivityStats] = useState({
    favorites: 0,
    trips: 0,
    visits: 0,
    comments: 0,
    saved: 0
  });
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedPostIds, setSavedPostIds] = useState([]); // IDs of saved posts
  const [visitedPostIds, setVisitedPostIds] = useState([]); // IDs of visited posts
  const [visitedPosts, setVisitedPosts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [taggedPosts, setTaggedPosts] = useState([]);
  const [taggedLoading, setTaggedLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedPosts, setFeedPosts] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('posts'); // posts, saved, tagged, activities
  const [activeView, setActiveView] = useState('main'); // main, settings, notifications, privacy, help, editProfile, changePassword, inbox
  
  // Inbox/Messages state
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendingChatMessage, setSendingChatMessage] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null); // For post detail modal
  const [currentPostIndex, setCurrentPostIndex] = useState(0); // For navigating posts
  const [postsViewMode, setPostsViewMode] = useState('grid'); // 'grid' or 'feed'
  const [showComments, setShowComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // Post ID to delete
  const [deleting, setDeleting] = useState(false);
  const [coverImage, setCoverImage] = useState(null); // Cover/banner image
  const [showCoverOptions, setShowCoverOptions] = useState(false);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // Notification & Privacy settings
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationSettings, setNotificationSettings] = useState({
    likes: true, comments: true, follows: true, messages: true
  });
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public', // 1A: public or private
    showOnline: true, // 2A: true or false
    followerListVisibility: 'followers', // 3B: everyone, followers, none
    showActivity: true // 4A: true or false
  });
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  
  // Profile edit states
  const [editForm, setEditForm] = useState({
    name: '', username: '', bio: '', phone: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false, new: false, confirm: false
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: '', verificationCode: ''
  });
  const [emailChangeStep, setEmailChangeStep] = useState(1); // 1: enter email, 2: enter code
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-white';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
      fetchPosts();
      fetchSavedPosts();
      fetchVisitedPosts();
      fetchFavorites();
      loadProfileImage();
      fetchUnreadMessagesCount();
      fetchNotifications(); // Fetch notifications from backend
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'tagged') {
      fetchTaggedPosts();
    }
  }, [isAuthenticated, activeTab]);

  // Check if we should open a specific view (from header icons)
  useEffect(() => {
    const profileView = sessionStorage.getItem('profileView');
    if (profileView) {
      sessionStorage.removeItem('profileView'); // Clear it immediately
      if (profileView === 'notifications') {
        setActiveView('notifications');
        fetchNotifications();
      } else if (profileView === 'settings') {
        setShowMenu(true);
      } else if (profileView === 'privacy') {
        setActiveView('privacy');
      } else if (profileView === 'editProfile') {
        setActiveView('settings');
      } else if (profileView === 'help') {
        setActiveView('help');
      }
    }
  }, []);

  const persistPrivacySettings = async (next) => {
    setPrivacySettings(next);
    setSavingPrivacy(true);
    try {
      await userApi.updateProfile({ privacySettings: next });
    } catch (e) {
      console.error('Gizlilik ayarları kaydedilemedi:', e);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const fetchTaggedPosts = async () => {
    try {
      setTaggedLoading(true);
      const data = await communityApi.getTaggedPosts(user?.id);
      setTaggedPosts(data || []);
    } catch (e) {
      console.error('Etiketlenen gönderiler yüklenemedi:', e);
      setTaggedPosts([]);
    } finally {
      setTaggedLoading(false);
    }
  };


  const loadProfileImage = async () => {
    // First try to load from backend
    try {
      const profile = await userApi.getProfile();
      if (profile?.avatar) {
        setProfileImage(profile.avatar);
        localStorage.setItem('profileImage', profile.avatar);
      }
      if (profile?.coverImage) {
        setCoverImage(profile.coverImage);
        localStorage.setItem('coverImage', profile.coverImage);
      }
      if (profile?.privacySettings) {
        setPrivacySettings({
          profileVisibility: profile.privacySettings.profileVisibility || 'public',
          showOnline: profile.privacySettings.showOnline !== false,
          followerListVisibility: profile.privacySettings.followerListVisibility || 'followers',
          showActivity: profile.privacySettings.showActivity !== false
        });
      }

      // Update user context with backend data
      if (profile && setUser) {
        setUser(prev => ({
          ...prev,
          name: profile.name || prev?.name,
          username: profile.username || prev?.name?.toLowerCase().replace(/\s/g, '.'),
          bio: profile.bio,
          avatar: profile.avatar,
          coverImage: profile.coverImage,
          followers: profile.followers,
          following: profile.following
        }));
      }
    } catch (error) {
      console.error('Profil yüklenemedi:', error);
    }
    // Fallback to localStorage
    const savedImage = localStorage.getItem('profileImage');
    if (savedImage && !profileImage) setProfileImage(savedImage);
    const savedCover = localStorage.getItem('coverImage');
    if (savedCover && !coverImage) setCoverImage(savedCover);
  };

  const fetchUserData = async () => {
    try {
      // Fetch profile data from backend
      const profile = await userApi.getProfile();
      
      // Fetch posts to count
      const postsData = await communityApi.getPosts();
      const userPosts = postsData.filter(p => p.authorId === user?.id);
      
      setStats({
        posts: userPosts.length,
        followers: profile?.followers || 0,
        following: profile?.following || 0
      });
    } catch (error) {
      setStats({ posts: 0, followers: 128, following: 89 });
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await communityApi.getPosts();
      const userPosts = data.filter(p => p.authorId === user?.id);
      setPosts(userPosts);
      setStats(prev => ({ ...prev, posts: userPosts.length }));
    } catch (error) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedPosts = async () => {
    try {
      const data = await communityApi.getSavedPosts();
      setSavedPosts(data);
      setActivityStats(prev => ({ ...prev, saved: data.length }));
    } catch (error) {
      setSavedPosts([]);
    }
  };

  const fetchVisitedPosts = async () => {
    try {
      const data = await communityApi.getVisitedPosts();
      setVisitedPosts(data);
      setActivityStats(prev => ({ ...prev, visits: data.length }));
    } catch (error) {
      setVisitedPosts([]);
    }
  };

  const fetchFavorites = async () => {
    try {
      const data = await favoritesApi.getAll();
      setFavorites(data);
      setActivityStats(prev => ({ ...prev, favorites: data.length }));
    } catch (error) {
      setFavorites([]);
    }
  };

  // Activity card navigation handler
  const handleActivityClick = (activityType) => {
    switch (activityType) {
      case 'Favorilerim':
        navigate('/favorites');
        break;
      case 'Gezilerim':
        if (onTabChange) {
          onTabChange('trips');
        } else {
          navigate('/explore'); // Fallback if no tab change handler
        }
        break;
      case 'Ziyaretler':
        setActiveTab('saved'); // Show visited in saved tab
        break;
      case 'Paylaşımlar':
        setActiveTab('posts');
        break;
      case 'Yorumlar':
        if (onTabChange) {
          onTabChange('community');
        }
        break;
      case 'Kaydedilen':
        setActiveTab('saved');
        break;
      default:
        break;
    }
  };

  // Post interaction functions
  const toggleVisited = async (postId) => {
    if (!isAuthenticated) return;
    
    const isVisited = visitedPostIds.includes(postId);
    setVisitedPostIds(prev => 
      isVisited ? prev.filter(id => id !== postId) : [...prev, postId]
    );
    
    // Update post in local state
    setPosts(prev => prev.map(p => 
      p.id === postId ? { ...p, visited: isVisited ? (p.visited || 1) - 1 : (p.visited || 0) + 1 } : p
    ));
    
    try {
      await communityApi.toggleVisited(postId);
    } catch (error) {
      // Revert on error
      setVisitedPostIds(prev => 
        isVisited ? [...prev, postId] : prev.filter(id => id !== postId)
      );
    }
  };

  const toggleSave = async (postId) => {
    if (!isAuthenticated) return;
    
    const isSaved = savedPostIds.includes(postId);
    setSavedPostIds(prev => 
      isSaved ? prev.filter(id => id !== postId) : [...prev, postId]
    );
    
    try {
      await communityApi.toggleSave(postId);
      // Refresh saved posts if on saved tab
      if (activeTab === 'saved') {
        fetchSavedPosts();
      }
    } catch (error) {
      setSavedPostIds(prev => 
        isSaved ? [...prev, postId] : prev.filter(id => id !== postId)
      );
    }
  };

  const handleAddToRoute = (post) => {
    if (onTabChange) {
      onTabChange('trips');
    }
  };

  const openComments = async (postId) => {
    setShowComments(postId);
    try {
      const data = await communityApi.getComments(postId);
      setComments(data);
    } catch (error) {
      setComments([]);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !showComments) return;
    
    try {
      await communityApi.addComment(showComments, newComment.trim());
      setComments(prev => [...prev, {
        id: Date.now(),
        authorName: user?.name || 'Sen',
        content: newComment.trim(),
        timestamp: 'Az önce'
      }]);
      setNewComment('');
      // Update comment count
      setPosts(prev => prev.map(p => 
        p.id === showComments ? { ...p, comments: (p.comments || 0) + 1 } : p
      ));
    } catch (error) {
      console.error('Yorum eklenemedi:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    setDeleting(true);
    try {
      await communityApi.deletePost(postId);
      // Remove from local state
      setPosts(prev => prev.filter(p => p.id !== postId));
      setStats(prev => ({ ...prev, posts: prev.posts - 1 }));
      setShowDeleteConfirm(null);
      // Close post detail modal if open
      if (selectedPost?.id === postId) {
        setSelectedPost(null);
      }
    } catch (error) {
      console.error('Gönderi silinemedi:', error);
      alert('Gönderi silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  // Inbox/Messages functions
  const fetchConversations = async () => {
    setConversationsLoading(true);
    try {
      const data = await messagesApi.getConversations();
      setConversations(data || []);
    } catch (error) {
      console.error('Sohbetler yüklenemedi:', error);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const openConversation = async (conv) => {
    setSelectedConversation(conv);
    setChatMessages([]);
    try {
      const messages = await messagesApi.getMessages(conv.userId);
      setChatMessages(messages || []);
    } catch (error) {
      console.error('Mesajlar yüklenemedi:', error);
    }
  };

  const handleSendChatMessage = async () => {
    if (!newChatMessage.trim() || !selectedConversation || sendingChatMessage) return;
    
    setSendingChatMessage(true);
    try {
      const result = await messagesApi.sendMessage(selectedConversation.userId, newChatMessage.trim());
      if (result.success && result.message) {
        setChatMessages(prev => [...prev, result.message]);
        setNewChatMessage('');
        // Update last message in conversations list
        setConversations(prev => prev.map(c => 
          c.userId === selectedConversation.userId 
            ? { ...c, lastMessage: newChatMessage.trim(), lastMessageTime: new Date().toISOString() }
            : c
        ));
      }
    } catch (error) {
      console.error('Mesaj gönderilemedi:', error);
      alert('Mesaj gönderilemedi');
    } finally {
      setSendingChatMessage(false);
    }
  };

  const openInbox = () => {
    setActiveView('inbox');
    fetchConversations();
    setUnreadMessagesCount(0); // Reset badge when opening inbox
  };

  // Fetch unread messages count
  const fetchUnreadMessagesCount = async () => {
    if (!isAuthenticated) return;
    try {
      const convs = await messagesApi.getConversations();
      const totalUnread = (convs || []).reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    if (!isAuthenticated) return;
    setNotificationsLoading(true);
    try {
      const data = await notificationsApi.getNotifications();
      setNotifications(data || []);
      // Update unread count from notifications
      const unread = (data || []).filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Mark notification as read and navigate
  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.read) {
      try {
        await notificationsApi.markAsRead(notification.id);
        setNotifications(prev => prev.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Navigate based on notification type
    const data = notification.data || {};
    switch (notification.type) {
      case 'follow':
        // Go to community to see the follower
        if (onTabChange) onTabChange('community');
        break;
      case 'like':
      case 'comment':
        // Go to community to see the post
        if (onTabChange) onTabChange('community');
        break;
      case 'message':
        // Open inbox
        openInbox();
        break;
      default:
        break;
    }
    setActiveView('main');
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        setProfileImage(base64Image);
        localStorage.setItem('profileImage', base64Image);
        setShowImageOptions(false);
        
        // Save to backend
        try {
          await userApi.updateProfile({ avatar: base64Image });
        } catch (error) {
          console.error('Profil fotoğrafı kaydedilemedi:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = async () => {
    setProfileImage(null);
    localStorage.removeItem('profileImage');
    setShowImageOptions(false);
    
    // Remove from backend
    try {
      await userApi.updateProfile({ avatar: null });
    } catch (error) {
      console.error('Profil fotoğrafı silinemedi:', error);
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        setCoverImage(base64Image);
        localStorage.setItem('coverImage', base64Image);
        setShowCoverOptions(false);
        
        // Save to backend
        try {
          await userApi.updateProfile({ coverImage: base64Image });
        } catch (error) {
          console.error('Kapak fotoğrafı kaydedilemedi:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCover = async () => {
    setCoverImage(null);
    localStorage.removeItem('coverImage');
    setShowCoverOptions(false);
    
    try {
      await userApi.updateProfile({ coverImage: null });
    } catch (error) {
      console.error('Kapak fotoğrafı silinemedi:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updateData = {
        name: editForm.name,
        username: editForm.username,
        bio: editForm.bio,
        phone: editForm.phone
      };
      
      const response = await userApi.updateProfile(updateData);
      
      if (response.success) {
        // Update user context
        if (setUser) {
          setUser(prev => ({
            ...prev,
            name: editForm.name,
            username: editForm.username,
            bio: editForm.bio,
            phone: editForm.phone
          }));
        }
        alert('Profil başarıyla güncellendi!');
        setActiveView('settings');
      }
    } catch (error) {
      console.error('Profil güncellenemedi:', error);
      alert(error.response?.data?.detail || 'Profil güncellenirken bir hata oluştu');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRequestEmailChange = async () => {
    if (!emailForm.newEmail) {
      alert('Lütfen yeni e-posta adresini girin');
      return;
    }
    
    setSavingEmail(true);
    try {
      const response = await userApi.requestEmailChange(emailForm.newEmail);
      if (response.success) {
        setEmailChangeStep(2);
        alert('Doğrulama kodu yeni e-posta adresinize gönderildi');
      }
    } catch (error) {
      console.error('E-posta değiştirme hatası:', error);
      alert(error.response?.data?.detail || 'E-posta değiştirme isteği gönderilemedi');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    if (!emailForm.verificationCode) {
      alert('Lütfen doğrulama kodunu girin');
      return;
    }
    
    setSavingEmail(true);
    try {
      const response = await userApi.verifyEmailChange(emailForm.verificationCode, emailForm.newEmail);
      if (response.success) {
        // Update user context with new email
        if (setUser) {
          setUser(prev => ({ ...prev, email: response.email }));
        }
        alert('E-posta adresiniz başarıyla güncellendi!');
        setEmailForm({ newEmail: '', verificationCode: '' });
        setEmailChangeStep(1);
        setActiveView('settings');
      }
    } catch (error) {
      console.error('E-posta doğrulama hatası:', error);
      alert(error.response?.data?.detail || 'Doğrulama kodu geçersiz');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTabChange = (tab) => {
    if (onTabChange) onTabChange(tab);
  };

  // Not authenticated view
  if (!isAuthenticated) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center p-4`}>
        <div className="text-center">
          <div className={`w-24 h-24 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <User className={`w-12 h-12 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
          </div>
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Profilinize Erişin</h2>
          <p className={`${textSecondary} mb-6`}>Giriş yaparak tüm özelliklere ulaşın</p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium"
          >
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }



  // Settings/Menu Drawer
  if (showMenu) {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className={`${bgCard} border-b ${borderColor} sticky top-0 z-10`}>
            <div className="px-4 py-3 flex items-center justify-between">
              <h1 className={`text-xl font-bold ${textPrimary}`}>Ayarlar</h1>
              <button onClick={() => setShowMenu(false)} className={`p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full`}>
                <X className={`w-6 h-6 ${textPrimary}`} />
              </button>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-4 space-y-2">
            {/* Business Owner Panel - Only show if user is business owner */}
            {user?.role === 'business_owner' && user?.owned_business_id && (
              <button
                onClick={() => { setShowMenu(false); if (onTabChange) onTabChange('business-panel'); }}
                className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} border-2 border-emerald-500`}
              >
                <div className={`p-2 rounded-full bg-emerald-500`}>
                  <Building2 className={`w-5 h-5 text-white`} />
                </div>
                <span className={`flex-1 text-left font-medium text-emerald-500`}>İşletmemi Yönet</span>
                <ChevronRight className={`w-5 h-5 text-emerald-500`} />
              </button>
            )}

            {/* Settings */}
            <button
              onClick={() => { setShowMenu(false); setActiveView('settings'); }}
              className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <Settings className={`w-5 h-5 ${textSecondary}`} />
              </div>
              <span className={`flex-1 text-left font-medium ${textPrimary}`}>Hesap Ayarları</span>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>

            {/* Privacy */}
            <button
              onClick={() => { setShowMenu(false); setActiveView('privacy'); }}
              className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-2 rounded-full ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                <Shield className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              </div>
              <span className={`flex-1 text-left font-medium ${textPrimary}`}>Gizlilik</span>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>

            {/* Dark Mode Toggle */}
            <div className={`flex items-center gap-4 p-4 ${bgCard} rounded-xl`}>
              <div className={`p-2 rounded-full ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                {darkMode ? <Moon className="w-5 h-5 text-yellow-400" /> : <Sun className="w-5 h-5 text-yellow-500" />}
              </div>
              <span className={`flex-1 font-medium ${textPrimary}`}>Koyu Tema</span>
              <button
                onClick={toggleDarkMode}
                className={`w-12 h-7 rounded-full transition-colors ${darkMode ? 'bg-teal-500' : 'bg-gray-300'} relative`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Help */}
            <button
              onClick={() => { setShowMenu(false); setActiveView('help'); }}
              className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-2 rounded-full ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                <HelpCircle className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
              </div>
              <span className={`flex-1 text-left font-medium ${textPrimary}`}>Yardım & Destek</span>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
            >
              <div className={`p-2 rounded-full ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                <LogOut className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
              </div>
              <span className={`flex-1 text-left font-medium text-red-500`}>Çıkış Yap</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Notifications View
  if (activeView === 'notifications') {
    // Calculate time ago
    const getTimeAgo = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Az önce';
      if (diffMins < 60) return `${diffMins} dakika önce`;
      if (diffHours < 24) return `${diffHours} saat önce`;
      if (diffDays < 7) return `${diffDays} gün önce`;
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    };

    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader 
          title="Bildirimler" 
          onBack={() => setActiveView('main')}
          rightAction={
            notifications.some(n => !n.read) && (
              <button 
                onClick={markAllNotificationsRead}
                className="text-teal-500 text-sm font-medium"
              >
                Tümünü Okundu İşaretle
              </button>
            )
          }
        />
        <div className="max-w-3xl mx-auto p-4 space-y-2">
          {notificationsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className={`w-16 h-16 ${textSecondary} mb-4`} />
              <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Bildirim Yok</h3>
              <p className={`${textSecondary} text-sm`}>Yeni bildirimleriniz burada görünecek</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full flex items-start gap-3 p-4 rounded-xl text-left transition-all ${
                  !notif.read 
                    ? (darkMode ? 'bg-teal-900/30 hover:bg-teal-900/40' : 'bg-teal-50 hover:bg-teal-100') 
                    : `${bgCard} ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} opacity-70`
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  notif.read 
                    ? (darkMode ? 'bg-slate-700' : 'bg-gray-200') 
                    : (darkMode ? 'bg-slate-600' : 'bg-white shadow')
                }`}>
                  {notif.type === 'like' && <Heart className={`w-5 h-5 ${notif.read ? 'text-red-400' : 'text-red-500'}`} />}
                  {notif.type === 'comment' && <MessageCircle className={`w-5 h-5 ${notif.read ? 'text-blue-400' : 'text-blue-500'}`} />}
                  {notif.type === 'follow' && <Users className={`w-5 h-5 ${notif.read ? 'text-green-400' : 'text-green-500'}`} />}
                  {notif.type === 'message' && <MessageSquare className={`w-5 h-5 ${notif.read ? 'text-teal-400' : 'text-teal-500'}`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notif.read ? textSecondary : textPrimary}`}>{notif.message}</p>
                  <p className={`text-xs mt-1 ${textSecondary}`}>{getTimeAgo(notif.created_at)}</p>
                </div>
                {!notif.read && (
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2" />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // Settings View
  if (activeView === 'settings') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader title="Hesap Ayarları" onBack={() => setActiveView('main')} />
        <div className="max-w-3xl mx-auto p-4 space-y-3">
          <button
            onClick={() => { setEditForm({ name: user?.name || '', username: user?.username || user?.name?.toLowerCase().replace(/\s/g, '.') || '', bio: user?.bio || '', phone: user?.phone || '' }); setActiveView('editProfile'); }}
            className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl`}
          >
            <Edit3 className={`w-5 h-5 ${textSecondary}`} />
            <span className={`flex-1 text-left ${textPrimary}`}>Profili Düzenle</span>
            <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
          </button>
          <button
            onClick={() => setActiveView('changePassword')}
            className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl`}
          >
            <Lock className={`w-5 h-5 ${textSecondary}`} />
            <span className={`flex-1 text-left ${textPrimary}`}>Şifre Değiştir</span>
            <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
          </button>
          <button
            onClick={() => { setEmailForm({ newEmail: '', verificationCode: '' }); setEmailChangeStep(1); setActiveView('changeEmail'); }}
            className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl`}
          >
            <Mail className={`w-5 h-5 ${textSecondary}`} />
            <div className="flex-1 text-left">
              <span className={textPrimary}>E-posta Değiştir</span>
              <p className={`text-sm ${textSecondary}`}>{user?.email}</p>
            </div>
            <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
          </button>
        </div>
      </div>
    );
  }

  // Edit Profile View
  if (activeView === 'editProfile') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader 
          title="Profili Düzenle" 
          onBack={() => setActiveView('settings')}
          rightAction={
            <button 
              onClick={handleSaveProfile} 
              disabled={savingProfile}
              className="text-teal-500 font-semibold disabled:opacity-50"
            >
              {savingProfile ? (
                <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-6 h-6" />
              )}
            </button>
          }
        />
        <div className="max-w-3xl mx-auto p-4 space-y-4">
          {/* Profile Photo */}
          <div className="flex flex-col items-center py-4">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#1e3a5f] to-[#0d9488]">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-3xl font-bold">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
              </div>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="mt-2 text-teal-500 font-medium text-sm">
              Fotoğrafı Değiştir
            </button>
            <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className={`text-sm ${textSecondary} mb-1 block`}>Ad Soyad</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
              />
            </div>
            <div>
              <label className={`text-sm ${textSecondary} mb-1 block`}>Kullanıcı Adı</label>
              <input
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
                placeholder="@kullaniciadi"
              />
            </div>
            <div>
              <label className={`text-sm ${textSecondary} mb-1 block`}>Biyografi</label>
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                rows={3}
                className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none resize-none`}
                placeholder="Kendinizden bahsedin..."
              />
            </div>
            <div>
              <label className={`text-sm ${textSecondary} mb-1 block`}>Telefon</label>
              <input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
                placeholder="+90 5XX XXX XX XX"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Change Password View
  if (activeView === 'changePassword') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader title="Şifre Değiştir" onBack={() => setActiveView('settings')} />
        <div className="max-w-3xl mx-auto p-4 space-y-4">
          <div>
            <label className={`text-sm ${textSecondary} mb-1 block`}>Mevcut Şifre</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className={`w-full p-3 pr-12 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
              />
              <button onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })} className="absolute right-3 top-1/2 -translate-y-1/2">
                {showPasswords.current ? <EyeOff className={`w-5 h-5 ${textSecondary}`} /> : <Eye className={`w-5 h-5 ${textSecondary}`} />}
              </button>
            </div>
          </div>
          <div>
            <label className={`text-sm ${textSecondary} mb-1 block`}>Yeni Şifre</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className={`w-full p-3 pr-12 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
              />
              <button onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })} className="absolute right-3 top-1/2 -translate-y-1/2">
                {showPasswords.new ? <EyeOff className={`w-5 h-5 ${textSecondary}`} /> : <Eye className={`w-5 h-5 ${textSecondary}`} />}
              </button>
            </div>
          </div>
          <div>
            <label className={`text-sm ${textSecondary} mb-1 block`}>Yeni Şifre (Tekrar)</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`w-full p-3 pr-12 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
              />
              <button onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })} className="absolute right-3 top-1/2 -translate-y-1/2">
                {showPasswords.confirm ? <EyeOff className={`w-5 h-5 ${textSecondary}`} /> : <Eye className={`w-5 h-5 ${textSecondary}`} />}
              </button>
            </div>
          </div>
          <button className="w-full py-3 bg-teal-500 text-white rounded-xl font-medium mt-4">
            Şifreyi Güncelle
          </button>
        </div>
      </div>
    );
  }

  // Change Email View
  if (activeView === 'changeEmail') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader title="E-posta Değiştir" onBack={() => setActiveView('settings')} />
        <div className="max-w-3xl mx-auto p-4 space-y-4">
          {/* Current Email */}
          <div className={`p-4 ${bgCard} rounded-xl`}>
            <p className={`text-sm ${textSecondary}`}>Mevcut E-posta</p>
            <p className={`font-medium ${textPrimary}`}>{user?.email}</p>
          </div>

          {emailChangeStep === 1 ? (
            <>
              {/* New Email Input */}
              <div>
                <label className={`text-sm ${textSecondary} mb-1 block`}>Yeni E-posta Adresi</label>
                <input
                  type="email"
                  value={emailForm.newEmail}
                  onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                  className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none`}
                  placeholder="yeni@email.com"
                />
              </div>

              <p className={`text-sm ${textSecondary}`}>
                Yeni e-posta adresinize bir doğrulama kodu göndereceğiz.
              </p>

              <button 
                onClick={handleRequestEmailChange}
                disabled={savingEmail || !emailForm.newEmail}
                className="w-full py-3 bg-teal-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingEmail ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Gönderiliyor...
                  </>
                ) : 'Doğrulama Kodu Gönder'}
              </button>
            </>
          ) : (
            <>
              {/* Verification Code Input */}
              <div className={`p-4 ${darkMode ? 'bg-green-900/20' : 'bg-green-50'} rounded-xl`}>
                <p className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  ✓ Doğrulama kodu <strong>{emailForm.newEmail}</strong> adresine gönderildi
                </p>
              </div>

              <div>
                <label className={`text-sm ${textSecondary} mb-1 block`}>Doğrulama Kodu</label>
                <input
                  type="text"
                  value={emailForm.verificationCode}
                  onChange={(e) => setEmailForm({ ...emailForm, verificationCode: e.target.value })}
                  className={`w-full p-3 rounded-xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-900'} outline-none text-center text-2xl tracking-widest`}
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              <button 
                onClick={handleVerifyEmailChange}
                disabled={savingEmail || emailForm.verificationCode.length < 6}
                className="w-full py-3 bg-teal-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingEmail ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : 'E-postayı Güncelle'}
              </button>

              <button 
                onClick={() => setEmailChangeStep(1)}
                className={`w-full py-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} ${textPrimary} rounded-xl font-medium`}
              >
                Farklı E-posta Gir
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Privacy View
  if (activeView === 'privacy') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader title="Gizlilik" onBack={() => setActiveView('main')} />
        <div className="max-w-3xl mx-auto p-4 space-y-4">
          {savingPrivacy && (
            <div className={`text-sm ${textSecondary} px-1 flex items-center gap-2`}>
              <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              Kaydediliyor...
            </div>
          )}
          
          {/* 1. Profile Visibility (1A - Public) */}
          <div className={`p-4 ${bgCard} rounded-xl`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className={`font-semibold ${textPrimary} mb-1`}>1. Profil Görünürlüğü</p>
                <p className={`text-sm ${textSecondary} mb-3`}>Profilinizin kimler tarafından görülebileceğini belirleyin</p>
                
                <div className="space-y-2">
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, profileVisibility: 'public' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.profileVisibility === 'public' || !privacySettings.profileVisibility
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.profileVisibility === 'public' || !privacySettings.profileVisibility
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {(privacySettings.profileVisibility === 'public' || !privacySettings.profileVisibility) && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>A. Herkese Açık</p>
                        <p className={`text-sm ${textSecondary}`}>Herkes profilinizi görüntüleyebilir</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, profileVisibility: 'private' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.profileVisibility === 'private'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.profileVisibility === 'private'
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.profileVisibility === 'private' && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>B. Gizli</p>
                        <p className={`text-sm ${textSecondary}`}>Sadece takipçileriniz profilinizi görebilir</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Online Status (2A - On) */}
          <div className={`p-4 ${bgCard} rounded-xl`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className={`font-semibold ${textPrimary} mb-1`}>2. Çevrimiçi Durum</p>
                <p className={`text-sm ${textSecondary} mb-3`}>Çevrimiçi olduğunuzda durumunuz görünsün mü?</p>
                
                <div className="space-y-2">
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, showOnline: true })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.showOnline !== false
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.showOnline !== false
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.showOnline !== false && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>A. Açık</p>
                        <p className={`text-sm ${textSecondary}`}>Diğer kullanıcılar çevrimiçi durumunuzu görebilir</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, showOnline: false })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.showOnline === false
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.showOnline === false
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.showOnline === false && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>B. Kapalı</p>
                        <p className={`text-sm ${textSecondary}`}>Çevrimiçi durumunuz gizli kalır</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Follower List Visibility (3B - Followers Only) */}
          <div className={`p-4 ${bgCard} rounded-xl`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className={`font-semibold ${textPrimary} mb-1`}>3. Takipçi Listesi Görünürlüğü</p>
                <p className={`text-sm ${textSecondary} mb-3`}>Takipçi listenizi kimler görebilir?</p>
                
                <div className="space-y-2">
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, followerListVisibility: 'everyone' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.followerListVisibility === 'everyone'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.followerListVisibility === 'everyone'
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.followerListVisibility === 'everyone' && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>A. Herkes</p>
                        <p className={`text-sm ${textSecondary}`}>Tüm kullanıcılar takipçi listenizi görebilir</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, followerListVisibility: 'followers' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.followerListVisibility === 'followers' || !privacySettings.followerListVisibility
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.followerListVisibility === 'followers' || !privacySettings.followerListVisibility
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {(privacySettings.followerListVisibility === 'followers' || !privacySettings.followerListVisibility) && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>B. Sadece Takipçilerim</p>
                        <p className={`text-sm ${textSecondary}`}>
                          {privacySettings.profileVisibility === 'private' 
                            ? 'Gizli hesapta sadece takipleştikleriniz görebilir'
                            : 'Sadece sizi takip edenler görebilir'}
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, followerListVisibility: 'none' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.followerListVisibility === 'none'
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.followerListVisibility === 'none'
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.followerListVisibility === 'none' && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>C. Kimse</p>
                        <p className={`text-sm ${textSecondary}`}>Takipçi listeniz tamamen gizli</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Activity Status (4A - On) */}
          <div className={`p-4 ${bgCard} rounded-xl`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <p className={`font-semibold ${textPrimary} mb-1`}>4. Etkinlik Durumu</p>
                <p className={`text-sm ${textSecondary} mb-3`}>Aktiviteleriniz (beğeniler, yorumlar) diğerlerine görünsün mü?</p>
                
                <div className="space-y-2">
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, showActivity: true })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.showActivity !== false
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.showActivity !== false
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.showActivity !== false && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>A. Açık</p>
                        <p className={`text-sm ${textSecondary}`}>Diğer kullanıcılar aktivitelerinizi görebilir</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => persistPrivacySettings({ ...privacySettings, showActivity: false })}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                      privacySettings.showActivity === false
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : `border-transparent ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        privacySettings.showActivity === false
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-400'
                      }`}>
                        {privacySettings.showActivity === false && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${textPrimary}`}>B. Kapalı</p>
                        <p className={`text-sm ${textSecondary}`}>Aktiviteleriniz gizli kalır</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Help View
  if (activeView === 'help') {
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        <SubPageHeader title="Yardım & Destek" onBack={() => setActiveView('main')} />
        <div className="max-w-3xl mx-auto p-4 space-y-3">
          {[
            { icon: FileText, label: 'Sıkça Sorulan Sorular' },
            { icon: MessageCircle, label: 'Bize Ulaşın' },
            { icon: Shield, label: 'Gizlilik Politikası' },
            { icon: FileText, label: 'Kullanım Koşulları' },
          ].map((item, idx) => (
            <button key={idx} className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl`}>
              <item.icon className={`w-5 h-5 ${textSecondary}`} />
              <span className={`flex-1 text-left ${textPrimary}`}>{item.label}</span>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Inbox/Messages View
  if (activeView === 'inbox') {
    return (
      <div className={`h-full ${bgMain} overflow-hidden pb-20 flex flex-col`}>
        <SubPageHeader 
          title={selectedConversation ? selectedConversation.userName : "Mesajlar"} 
          onBack={() => {
            if (selectedConversation) {
              setSelectedConversation(null);
              setChatMessages([]);
            } else {
              setActiveView('main');
            }
          }} 
        />
        
        {!selectedConversation ? (
          // Conversations List
          <div className="flex-1 overflow-auto">
            <div className="max-w-3xl mx-auto p-4 space-y-2">
              {conversationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <MessageCircle className={`w-16 h-16 ${textSecondary} mb-4`} />
                  <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Henüz mesajınız yok</h3>
                  <p className={`${textSecondary} text-sm`}>Topluluktaki kullanıcılara mesaj göndererek sohbet başlatın</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.userId}
                    onClick={() => openConversation(conv)}
                    className={`w-full flex items-center gap-4 p-4 ${bgCard} rounded-xl ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <div className="relative">
                      {conv.userAvatar ? (
                        <img src={conv.userAvatar} alt={conv.userName} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{conv.userName?.charAt(0) || 'K'}</span>
                        </div>
                      )}
                      {conv.unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h4 className={`font-semibold ${textPrimary} truncate`}>{conv.userName}</h4>
                      <p className={`text-sm ${textSecondary} truncate`}>{conv.lastMessage}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs ${textSecondary}`}>
                        {conv.lastMessageTime ? new Date(conv.lastMessageTime).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : ''}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          // Chat View
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-3xl mx-auto p-4 space-y-3">
              {chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <MessageCircle className={`w-12 h-12 ${textSecondary} mb-3`} />
                  <p className={`${textSecondary}`}>Henüz mesaj yok</p>
                  <p className={`text-sm ${textSecondary}`}>İlk mesajınızı gönderin!</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => {
                  const isOwnMessage = msg.senderId === user?.id;
                  const isLastOwnMessage = isOwnMessage && 
                    chatMessages.slice(idx + 1).every(m => m.senderId !== user?.id);
                  
                  return (
                    <div 
                      key={msg.id || idx}
                      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          isOwnMessage 
                            ? 'bg-teal-500 text-white rounded-br-md' 
                            : (darkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900') + ' rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isOwnMessage ? 'text-teal-100' : textSecondary}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                      {/* Görüldü indicator - only show on last own message if read */}
                      {isOwnMessage && isLastOwnMessage && msg.read && (
                        <div className={`flex items-center gap-1 mt-1 mr-1`}>
                          <CheckCircle2 className="w-3 h-3 text-teal-500" />
                          <span className={`text-xs ${textSecondary}`}>Görüldü</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              </div>
            </div>

            {/* Input */}
            <div className={`border-t ${borderColor} ${bgCard}`}>
              <div className="max-w-3xl mx-auto p-4">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newChatMessage}
                    onChange={(e) => setNewChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendChatMessage()}
                    placeholder="Mesajınızı yazın..."
                    className={`flex-1 px-4 py-3 rounded-full ${darkMode ? 'bg-slate-700 text-white placeholder-slate-400' : 'bg-gray-100 text-gray-900 placeholder-gray-400'} outline-none`}
                  />
                  <button
                    onClick={handleSendChatMessage}
                    disabled={!newChatMessage.trim() || sendingChatMessage}
                    className={`p-3 rounded-full transition-colors ${
                      newChatMessage.trim() && !sendingChatMessage
                        ? 'bg-teal-500 text-white hover:bg-teal-600'
                        : (darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400')
                    }`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main Profile View (Instagram Travel Style)
  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      <div className="max-w-3xl mx-auto">
        {/* Cover Image Section */}
        <div className="relative">
          {/* Cover Image */}
          <div 
            className="h-48 relative overflow-hidden cursor-pointer"
            onClick={() => { if (!showMenu && !coverImage) setShowCoverOptions(true); }}
          >
            {coverImage ? (
              <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              /* Default: Blurry green nature travel-themed background */
              <div 
                className="w-full h-full relative"
                style={{
                  background: 'linear-gradient(135deg, #1a472a 0%, #2d5a3d 25%, #1e4d2b 50%, #3a7d4a 75%, #2d5a3d 100%)',
                }}
              >
                {/* Blurred nature overlay effect */}
                <div 
                  className="absolute inset-0 opacity-40"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='blur'%3E%3CfeGaussianBlur stdDeviation='3'/%3E%3C/filter%3E%3Cg filter='url(%23blur)'%3E%3Ccircle cx='50' cy='80' r='60' fill='%234ade80' opacity='0.3'/%3E%3Ccircle cx='150' cy='50' r='80' fill='%2322c55e' opacity='0.25'/%3E%3Ccircle cx='280' cy='100' r='70' fill='%2316a34a' opacity='0.3'/%3E%3Ccircle cx='350' cy='60' r='50' fill='%234ade80' opacity='0.2'/%3E%3Ccircle cx='100' cy='200' r='90' fill='%2322c55e' opacity='0.2'/%3E%3Ccircle cx='300' cy='220' r='75' fill='%2316a34a' opacity='0.25'/%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: 'cover',
                    filter: 'blur(20px)',
                  }}
                />
                {/* Soft light spots for depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 via-transparent to-teal-600/30" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-300/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-500/15 rounded-full blur-3xl" />
              </div>
            )}
            {/* Gradient Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
          </div>
          <input type="file" ref={coverInputRef} onChange={handleCoverUpload} accept="image/*" className="hidden" />

          {/* Profile Info Card - Overlapping Cover */}
          <div className={`relative mx-4 -mt-16 ${bgCard} rounded-2xl shadow-lg p-4`}>
            <div className="flex items-start gap-4">
              {/* Profile Photo */}
              <div className="relative -mt-12">
                <button onClick={() => setShowImageOptions(true)}>
                  <div className="w-24 h-24 rounded-2xl overflow-hidden ring-4 ring-white dark:ring-slate-800 shadow-lg">
                    {profileImage ? (
                      <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] flex items-center justify-center text-white text-3xl font-bold">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center text-white shadow-md"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
              </div>

              {/* Username and Verified Badge */}
              <div className="flex-1 pt-2">
                <div className="flex items-center gap-2">
                  <h1 className={`text-lg font-bold ${textPrimary}`}>
                    {user?.name || 'Kullanıcı'}
                  </h1>
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
                {/* Username */}
                {user?.username && (
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'} mt-0.5`}>
                    @{user.username}
                  </p>
                )}
                
                {/* Stats Row */}
                <div className="flex items-center gap-4 mt-3">
                  <div className="text-center">
                    <p className={`text-lg font-bold ${textPrimary}`}>{stats.followers}</p>
                    <p className={`text-xs ${textSecondary}`}>Takipçi</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-lg font-bold ${textPrimary}`}>{stats.posts}</p>
                    <p className={`text-xs ${textSecondary}`}>Rota</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-lg font-bold ${textPrimary}`}>{favorites.length || 0}</p>
                    <p className={`text-xs ${textSecondary}`}>Mekan</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="mt-4">
              <p className={`text-sm ${textPrimary}`}>
                {user?.bio || 'Dünyayı keşfetmeyi seviyorum. Rotalarımı ve gizli mekanları paylaşıyorum. 🌍✨'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => setActiveView('settings')}
                className="flex-1 py-2.5 px-4 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors"
              >
                Profili Düzenle
              </button>
              <button 
                onClick={openInbox}
                className={`relative py-2.5 px-4 ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl ${textPrimary} transition-colors`}
              >
                <MessageSquare className="w-5 h-5" />
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </span>
                )}
              </button>
            </div>

            {/* Business Owner Quick Access */}
            {user?.role === 'business_owner' && user?.owned_business_id && (
              <button 
                onClick={() => { if (onTabChange) onTabChange('business-panel'); }}
                className="w-full mt-3 py-2.5 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Building2 className="w-5 h-5" />
                İşletmemi Yönet
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-t ${borderColor} mt-4`}>
          <button 
            aria-label="Gönderiler"
            onClick={() => setActiveTab('posts')}
            className={`flex-1 py-3 flex justify-center ${activeTab === 'posts' ? `border-b-2 ${darkMode ? 'border-white' : 'border-black'}` : ''}`}
          >
            <Grid3X3 className={`w-6 h-6 ${activeTab === 'posts' ? textPrimary : textSecondary}`} />
          </button>
          <button 
            aria-label="Kaydedilenler"
            onClick={() => setActiveTab('saved')}
            className={`flex-1 py-3 flex justify-center ${activeTab === 'saved' ? `border-b-2 ${darkMode ? 'border-white' : 'border-black'}` : ''}`}
          >
            <Bookmark className={`w-6 h-6 ${activeTab === 'saved' ? textPrimary : textSecondary}`} />
          </button>
          <button 
            aria-label="Etiketlenenler"
            onClick={() => setActiveTab('tagged')}
            className={`flex-1 py-3 flex justify-center ${activeTab === 'tagged' ? `border-b-2 ${darkMode ? 'border-white' : 'border-black'}` : ''}`}
          >
            <User className={`w-6 h-6 ${activeTab === 'tagged' ? textPrimary : textSecondary}`} />
          </button>
          <button 
            aria-label="Aktiviteler"
            onClick={() => setActiveTab('activities')}
            className={`flex-1 py-3 flex justify-center ${activeTab === 'activities' ? `border-b-2 ${darkMode ? 'border-white' : 'border-black'}` : ''}`}
          >
            <LayoutGrid className={`w-6 h-6 ${activeTab === 'activities' ? textPrimary : textSecondary}`} />
          </button>
        </div>

        {/* Posts Tab - Instagram Style Grid */}
        {activeTab === 'posts' && (
          <div>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : posts.length > 0 ? (
              <div className="grid grid-cols-3 gap-0.5">
                {posts.map((post, index) => (
                  <div 
                    key={post.id} 
                    className="aspect-square relative group cursor-pointer"
                    onClick={() => { setFeedPosts(posts); setSelectedPost(post); setCurrentPostIndex(index); }}
                  >
                    {post.image ? (
                      <img src={post.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className={`w-full h-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center p-2`}>
                        <p className={`text-xs ${textSecondary} text-center line-clamp-3`}>{post.content}</p>
                      </div>
                    )}
                    {/* Hover overlay with stats */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1 text-white">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-semibold">{post.visited || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white">
                        <MessageCircle className="w-5 h-5" />
                        <span className="font-semibold">{post.comments || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center`}>
                  <Camera className={`w-10 h-10 ${textSecondary}`} />
                </div>
                <h3 className={`text-xl font-bold ${textPrimary} mb-2`}>Henüz Gönderi Yok</h3>
                <p className={`${textSecondary} mb-4`}>İlk gönderinizi paylaşın!</p>
                <button 
                  onClick={() => handleTabChange('community')}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg font-medium"
                >
                  Paylaşım Yap
                </button>
              </div>
            )}
          </div>
        )}

        {/* Saved Tab */}
        {activeTab === 'saved' && (
          <div>
            {savedPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-0.5">
                {savedPosts.map((post, index) => (
                  <div 
                    key={post.id} 
                    className="aspect-square relative group cursor-pointer"
                    onClick={() => { setFeedPosts(savedPosts); setSelectedPost(post); setCurrentPostIndex(index); }}
                  >
                    {post.image ? (
                      <img src={post.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className={`w-full h-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center p-2`}>
                        <p className={`text-xs ${textSecondary} text-center line-clamp-3`}>{post.content}</p>
                      </div>
                    )}
                    {/* Saved indicator */}
                    <div className="absolute top-2 right-2">
                      <Bookmark className="w-4 h-4 text-white fill-white drop-shadow" />
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1 text-white">
                        <Heart className="w-5 h-5 fill-white" />
                        <span className="font-semibold">{post.likes || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white">
                        <MessageCircle className="w-5 h-5 fill-white" />
                        <span className="font-semibold">{post.comments || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center`}>
                  <Bookmark className={`w-10 h-10 ${textSecondary}`} />
                </div>
                <h3 className={`text-xl font-bold ${textPrimary} mb-2`}>Kaydedilenler</h3>
                <p className={`${textSecondary}`}>Kaydettiğiniz gönderiler burada görünecek</p>
              </div>
            )}
          </div>
        )}

        {/* Tagged Tab */}
        {activeTab === 'tagged' && (
          <div>
            {taggedLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : taggedPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-0.5">
                {taggedPosts.map((post, index) => (
                  <div 
                    key={post.id} 
                    className="aspect-square relative group cursor-pointer"
                    onClick={() => { setFeedPosts(taggedPosts); setSelectedPost(post); setCurrentPostIndex(index); }}
                  >
                    {post.image ? (
                      <img src={post.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className={`w-full h-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center p-2`}>
                        <p className={`text-xs ${textSecondary} text-center line-clamp-3`}>{post.content}</p>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1 text-white">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-semibold">{post.visited || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white">
                        <MessageCircle className="w-5 h-5" />
                        <span className="font-semibold">{post.comments || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-gray-100'} flex items-center justify-center`}>
                  <User className={`w-10 h-10 ${textSecondary}`} />
                </div>
                <h3 className={`text-xl font-bold ${textPrimary} mb-2`}>Etiketlenenler</h3>
                <p className={`${textSecondary}`}>Etiketlendiğiniz gönderiler burada görünecek</p>
              </div>
            )}
          </div>
        )}

        {/* Activities Tab - Card Grid Menu */}
        {activeTab === 'activities' && (
          <div className="p-4">
            <h3 className={`text-lg font-semibold ${textPrimary} mb-4`}>Aktivitelerim</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Heart, label: 'Favorilerim', count: activityStats.favorites, color: 'text-red-500', bgColor: darkMode ? 'bg-red-900/30' : 'bg-red-50' },
                { icon: MapPin, label: 'Gezilerim', count: activityStats.trips, color: 'text-blue-500', bgColor: darkMode ? 'bg-blue-900/30' : 'bg-blue-50' },
                { icon: Check, label: 'Ziyaretler', count: activityStats.visits, color: 'text-green-500', bgColor: darkMode ? 'bg-green-900/30' : 'bg-green-50' },
                { icon: MessageSquare, label: 'Paylaşımlar', count: stats.posts, color: 'text-purple-500', bgColor: darkMode ? 'bg-purple-900/30' : 'bg-purple-50' },
                { icon: Star, label: 'Yorumlar', count: activityStats.comments, color: 'text-yellow-500', bgColor: darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50' },
                { icon: Bookmark, label: 'Kaydedilen', count: activityStats.saved, color: 'text-teal-500', bgColor: darkMode ? 'bg-teal-900/30' : 'bg-teal-50' },
              ].map((item, idx) => (
                <button 
                  key={idx} 
                  onClick={() => handleActivityClick(item.label)}
                  className={`p-4 ${bgCard} rounded-2xl border ${borderColor} text-center hover:shadow-lg transition-all hover:scale-105 active:scale-95`}
                >
                  <div className={`w-12 h-12 rounded-full ${item.bgColor} flex items-center justify-center mx-auto mb-2`}>
                    <item.icon className={`w-6 h-6 ${item.color}`} />
                  </div>
                  <p className={`text-xs font-medium ${textSecondary} mb-1`}>{item.label}</p>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{item.count}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Post Detail Modal - Instagram Style Full Page Feed */}
      {selectedPost && (
        <div className={`fixed inset-0 ${bgMain} z-50 overflow-hidden`}>
          {/* Header - Instagram Style with Back Arrow */}
          <div className={`${bgCard} sticky top-0 z-10 border-b ${borderColor}`}>
            <div className="max-w-3xl mx-auto flex items-center px-4 py-3">
              <button 
                onClick={() => setSelectedPost(null)}
                className={`p-2 -ml-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full`}
              >
                <ChevronLeft className={`w-6 h-6 ${textPrimary}`} />
              </button>
              <h1 className={`flex-1 text-center text-lg font-semibold ${textPrimary}`}>Gönderiler</h1>
              <div className="w-10" /> {/* Spacer for centering */}
            </div>
          </div>

          {/* Scrollable Feed - Community Page Style */}
          <div className="h-[calc(100vh-56px)] overflow-y-auto pb-20">
            <div className="max-w-3xl mx-auto w-full">
            {(feedPosts.length ? feedPosts : posts).slice(currentPostIndex).concat((feedPosts.length ? feedPosts : posts).slice(0, currentPostIndex)).map((post) => (
              <div key={post.id} className={`${bgCard} border-b ${borderColor}`}>
                {/* Post Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center font-bold text-lg ${textPrimary}`}>
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${textPrimary}`}>{user?.name || 'Kullanıcı'}</span>
                        {post.category && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            post.category === 'visit' ? 'bg-blue-100 text-blue-600' :
                            post.category === 'food' ? 'bg-orange-100 text-orange-600' :
                            post.category === 'nature' ? 'bg-green-100 text-green-600' :
                            post.category === 'photo' ? 'bg-purple-100 text-purple-600' :
                            'bg-yellow-100 text-yellow-600'
                          }`}>
                            {post.category === 'visit' ? 'Mekan Ziyareti' :
                             post.category === 'food' ? 'Yemek Deneyimi' :
                             post.category === 'nature' ? 'Doğa Keşfi' :
                             post.category === 'photo' ? 'Fotoğraf Noktası' : 'Yerel İpucu'}
                          </span>
                        )}
                      </div>
                      {post.placeName && (
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className={`w-3 h-3 ${textSecondary}`} />
                          <span className={textSecondary}>{post.placeName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowDeleteConfirm(post.id)}
                    className={`p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full text-red-500`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Post Image */}
                {post.image && (
                  <div className="aspect-[4/3] bg-gray-100 relative">
                    <img src={post.image} alt="Post" className="w-full h-full object-cover" />
                    {/* Rating Badge */}
                    {post.rating > 0 && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-full">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-white text-sm font-medium">{post.rating}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Post Content */}
                <div className="p-4">
                  <p className={`text-sm ${textPrimary} mb-3`}>{post.content}</p>

                  {/* Travel-Specific Actions */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {/* Ben de Gittim */}
                      <button 
                        onClick={() => toggleVisited(post.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          visitedPostIds.includes(post.id)
                            ? 'bg-green-500 text-white'
                            : `${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary}`
                        }`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        {visitedPostIds.includes(post.id) ? 'Gittim' : 'Ben de Gittim'}
                      </button>
                      
                      {/* Rotama Ekle */}
                      <button 
                        onClick={() => handleAddToRoute(post)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} transition-colors`}
                      >
                        <Route className="w-4 h-4" />
                        Rotama Ekle
                      </button>
                    </div>
                    
                    {/* Save */}
                    <button 
                      onClick={() => toggleSave(post.id)}
                      className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                    >
                      <Bookmark className={`w-5 h-5 ${savedPostIds.includes(post.id) ? 'fill-[#1e3a5f] text-[#1e3a5f]' : textPrimary}`} />
                    </button>
                  </div>

                  {/* Stats */}
                  <div className={`flex items-center gap-4 text-sm ${textSecondary}`}>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {post.visited || 0} kişi gitti
                    </span>
                    <button onClick={() => openComments(post.id)} className="flex items-center gap-1 hover:underline">
                      <MessageCircle className="w-4 h-4" />
                      {post.comments || 0} yorum
                    </button>
                    <span className="flex items-center gap-1">
                      <Bookmark className="w-4 h-4" />
                      {post.saves || 0} kayıt
                    </span>
                  </div>

                  {/* Timestamp */}
                  <p className={`text-xs ${textSecondary} mt-2`}>{post.timestamp || 'Az önce'}</p>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

      {/* Image Options Modal */}
      {showImageOptions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setShowImageOptions(false)}>
          <div className={`${bgCard} w-full max-w-lg rounded-t-2xl p-4`} onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <h3 className={`text-center font-semibold ${textPrimary} mb-4`}>Profil Fotoğrafı</h3>
            <button onClick={() => { fileInputRef.current?.click(); setShowImageOptions(false); }} className={`w-full py-3 text-left ${textPrimary} font-medium`}>
              📷 Fotoğraf Yükle
            </button>
            {profileImage && (
              <button onClick={handleRemoveImage} className="w-full py-3 text-left text-red-500 font-medium">
                🗑️ Fotoğrafı Kaldır
              </button>
            )}
            <button onClick={() => setShowImageOptions(false)} className={`w-full py-3 text-left ${textSecondary}`}>
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Cover Image Options Modal */}
      {showCoverOptions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setShowCoverOptions(false)}>
          <div className={`${bgCard} w-full max-w-lg rounded-t-2xl p-4`} onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <h3 className={`text-center font-semibold ${textPrimary} mb-4`}>Kapak Fotoğrafı</h3>
            <button onClick={() => { coverInputRef.current?.click(); setShowCoverOptions(false); }} className={`w-full py-3 text-left ${textPrimary} font-medium`}>
              🖼️ Kapak Fotoğrafı Yükle
            </button>
            {coverImage && (
              <button onClick={handleRemoveCover} className="w-full py-3 text-left text-red-500 font-medium">
                🗑️ Kapak Fotoğrafını Kaldır
              </button>
            )}
            <button onClick={() => setShowCoverOptions(false)} className={`w-full py-3 text-left ${textSecondary}`}>
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center"
          onClick={() => setShowComments(null)}
        >
          <div 
            className={`${bgCard} w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[70vh] flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
              <h3 className={`font-semibold ${textPrimary}`}>Yorumlar</h3>
              <button onClick={() => setShowComments(null)}>
                <X className={`w-6 h-6 ${textPrimary}`} />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {comments.length === 0 ? (
                <p className={`text-center ${textSecondary} py-8`}>Henüz yorum yok. İlk yorumu siz yapın!</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <div className={`w-8 h-8 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center text-sm font-bold ${textPrimary}`}>
                      {comment.authorName?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${textPrimary}`}>
                        <span className="font-semibold">{comment.authorName}</span>{' '}
                        {comment.content}
                      </p>
                      <p className={`text-xs ${textSecondary} mt-1`}>{comment.timestamp}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Comment Input */}
            <div className={`p-4 border-t ${borderColor}`}>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  placeholder="Yorum ekle..."
                  className={`flex-1 px-4 py-2 rounded-full ${darkMode ? 'bg-slate-700 text-white placeholder-slate-400' : 'bg-gray-100 text-gray-900'} outline-none`}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className={`px-4 py-2 rounded-full font-medium ${newComment.trim() ? 'bg-teal-500 text-white' : `${darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'}`}`}
                >
                  Gönder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div 
            className={`${bgCard} w-full max-w-sm rounded-2xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className={`w-16 h-16 rounded-full ${darkMode ? 'bg-red-900/30' : 'bg-red-50'} flex items-center justify-center mx-auto mb-4`}>
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <h3 className={`text-lg font-bold ${textPrimary} mb-2`}>Gönderiyi Sil</h3>
              <p className={`${textSecondary} mb-6`}>Bu gönderiyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className={`flex-1 py-3 rounded-xl font-medium ${darkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                >
                  İptal
                </button>
                <button
                  onClick={() => handleDeletePost(showDeleteConfirm)}
                  disabled={deleting}
                  className="flex-1 py-3 rounded-xl font-medium bg-red-500 text-white disabled:opacity-50"
                >
                  {deleting ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
